import React, {useState, useEffect} from "react";
import {Form, Button, Card, CardBody, CardHeader, CardTitle, Row, Col, Alert} from "reactstrap";
import {signIn} from "aws-amplify/auth";
import {Link, useNavigate, useLocation} from "react-router-dom";
import AuthError from "./AuthError";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import {useUser} from "../../contexts/AuthContext";
import {rateLimiter} from "../../utils/rateLimiter";

interface LocationState {
  message?: string;
  email?: string;
}

const SignInForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useUser();
  const state = location.state as LocationState;

  const [email, setEmail] = useState(state?.email || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(state?.message || null);
  const [loading, setLoading] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [hadError, setHadError] = useState(false);

  // Refs to track DOM elements for password manager compatibility
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const passwordInputRef = React.useRef<HTMLInputElement>(null);

  // Clear location state after displaying message
  useEffect(() => {
    if (state?.message) {
      // Clear the state after showing the message
      window.history.replaceState({}, document.title);
    }
  }, [state?.message]);

  // Check rate limit status when email changes
  useEffect(() => {
    if (email) {
      const rateLimitKey = `signin_${email.toLowerCase()}`;
      const status = rateLimiter.checkRateLimit(rateLimitKey, {
        maxAttempts: 5,
        windowMs: 15 * 60 * 1000, // 15 minutes
        blockDurationMs: 60 * 60 * 1000 // 1 hour block for sign-in attempts
      });

      setIsRateLimited(status.isBlocked);
      if (status.isBlocked) {
        setRateLimitMessage(status.message || null);
      } else if (status.remainingAttempts < 3) {
        setRateLimitMessage(status.message || null);
      } else {
        setRateLimitMessage(null);
      }
    }
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Get actual values from DOM (password managers may have filled without triggering onChange)
    const actualEmail = emailInputRef.current?.value || email;
    const actualPassword = passwordInputRef.current?.value || password;

    // Update state for consistency
    if (actualEmail !== email) {
      setEmail(actualEmail);
    }
    if (actualPassword !== password) {
      setPassword(actualPassword);
    }

    // Validate using actual DOM values
    const errors: typeof validationErrors = {};

    if (!actualEmail) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(actualEmail)) {
      errors.email = "Please enter a valid email address";
    }

    if (!actualPassword) {
      errors.password = "Password is required";
    }

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Check rate limit before attempting sign-in
    const rateLimitKey = `signin_${actualEmail.toLowerCase()}`;
    const rateLimitStatus = rateLimiter.checkRateLimit(rateLimitKey, {
      maxAttempts: 5,
      windowMs: 15 * 60 * 1000,
      blockDurationMs: 60 * 60 * 1000
    });

    if (rateLimitStatus.isBlocked) {
      setError(rateLimitStatus.message || "Too many sign-in attempts. Please try again later.");
      setIsRateLimited(true);
      setRateLimitMessage(rateLimitStatus.message || null);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Dual-attempt sign-in strategy:
      // 1. Try with exact email as entered (supports existing mixed-case users)
      // 2. If UserNotFoundException, try lowercase (supports new lowercase-only users)
      let signInResult = null;
      try {
        signInResult = await signIn({
          username: actualEmail,
          password: actualPassword
        });
      } catch (firstError: any) {
        // If user not found and email contains uppercase, try lowercase
        if (firstError.name === "UserNotFoundException" && actualEmail !== actualEmail.toLowerCase()) {
          try {
            signInResult = await signIn({
              username: actualEmail.toLowerCase(),
              password: actualPassword
            });
          } catch {
            // Both attempts failed, throw the original error
            throw firstError;
          }
        } else {
          // Not a UserNotFoundException or already lowercase, throw original error
          throw firstError;
        }
      }

      const {isSignedIn, nextStep} = signInResult!;
      const normalizedEmail = actualEmail.toLowerCase();

      if (isSignedIn) {
        // Clear rate limit on successful sign-in
        rateLimiter.clearRateLimit(rateLimitKey);

        // Refresh the user context to get the JWT token
        window.location.href = "/upload"; // Full page reload to reinitialize AuthContext
      } else if ((nextStep as any)?.signInStep === "CONFIRM_SIGN_UP") {
        // User needs to verify their email
        localStorage.setItem("pendingVerificationEmail", normalizedEmail);
        navigate("/auth/verify", {state: {email: normalizedEmail}});
      } else if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        // User needs to set a new password
        navigate("/auth/new-password", {state: {email: normalizedEmail}});
      } else if ((nextStep as any)?.signInStep === "MFA_SETUP") {
        // User needs to set up MFA
        navigate("/auth/mfa-setup", {state: {email: normalizedEmail}});
      } else if ((nextStep as any)?.signInStep === "CONFIRM_MFA") {
        // User needs to provide MFA code
        navigate("/auth/mfa", {state: {email: normalizedEmail}});
      }
    } catch (err: any) {
      // Record failed attempt for rate limiting
      const attemptStatus = rateLimiter.recordAttempt(rateLimitKey, {
        maxAttempts: 5,
        windowMs: 15 * 60 * 1000,
        blockDurationMs: 60 * 60 * 1000
      });

      if (attemptStatus.isNowBlocked) {
        setIsRateLimited(true);
        setRateLimitMessage(attemptStatus.message || null);
      } else if (attemptStatus.remainingAttempts < 3) {
        setRateLimitMessage(attemptStatus.message || null);
      }

      //console.error("Sign in error:", err);

      // Mark that we had an error - we'll clear the form when user focuses on email field
      setHadError(true);

      // Handle specific Cognito errors
      if (err.name === "NotAuthorizedException") {
        setError("Incorrect email or password. Please try again.");
      } else if (err.name === "UserNotFoundException") {
        setError("No account found with this email.");
      } else if (err.name === "UserNotConfirmedException") {
        // User hasn't verified their email
        const normalizedEmail = email.toLowerCase();
        localStorage.setItem("pendingVerificationEmail", normalizedEmail);
        setError("Please verify your email before signing in.");
        setTimeout(() => {
          navigate("/auth/verify", {state: {email: normalizedEmail}});
        }, 2000);
      } else if (err.name === "PasswordResetRequiredException") {
        const normalizedEmail = email.toLowerCase();
        setError("Password reset is required. Redirecting...");
        setTimeout(() => {
          navigate("/auth/reset-password", {state: {email: normalizedEmail}});
        }, 2000);
      } else if (err.name === "TooManyFailedAttemptsException") {
        setError("Too many failed attempts. Please try again later.");
      } else {
        setError(err.message || "An error occurred during sign in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <>
          <div className="auth-alerts-container">
            <AuthError error={error} onClose={() => setError(null)} />

            {rateLimitMessage && !error && (
              <Alert color={isRateLimited ? "danger" : "warning"} className="mb-3">
                <span className="alert-inner--icon">
                  <i className={`tim-icons icon-${isRateLimited ? "alert-circle-exc" : "bulb-63"}`} />
                </span>
                <span className="alert-inner--text ml-1">{rateLimitMessage}</span>
              </Alert>
            )}

            {successMessage && (
              <Alert color="success" className="mb-3">
                <span className="alert-inner--icon">
                  <i className="tim-icons icon-check-2" />
                </span>
                <span className="alert-inner--text ml-1">{successMessage}</span>
              </Alert>
            )}
          </div>

          <Card className="auth-card">
            <CardHeader data-color={color}>
              <CardTitle tag="h3">Sign In</CardTitle>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">
                    Email Address
                    <span className="text-danger"> *</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className={`form-control form-control-alternative ${validationErrors.email ? "is-invalid" : ""}`}
                    placeholder="Enter your email"
                    autoComplete="email"
                    required
                    disabled={loading}
                    ref={emailInputRef}
                    onFocus={() => {
                      // If we had an error, clear the form when user focuses to allow password manager to refill
                      if (hadError) {
                        if (emailInputRef.current) {
                          emailInputRef.current.value = "";
                        }
                        if (passwordInputRef.current) {
                          passwordInputRef.current.value = "";
                        }
                        setEmail("");
                        setPassword("");
                        setHadError(false);
                      }
                    }}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (validationErrors.email) {
                        setValidationErrors({...validationErrors, email: undefined});
                      }
                    }}
                  />
                  {validationErrors.email && (
                    <div className="invalid-feedback" style={{display: "block"}}>
                      {validationErrors.email}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    Password
                    <span className="text-danger"> *</span>
                  </label>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className={`form-control form-control-alternative ${validationErrors.password ? "is-invalid" : ""}`}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                    disabled={loading}
                    ref={passwordInputRef}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (validationErrors.password) {
                        setValidationErrors({...validationErrors, password: undefined});
                      }
                    }}
                    onKeyPress={handleKeyPress}
                  />
                  {validationErrors.password && (
                    <div className="invalid-feedback" style={{display: "block"}}>
                      {validationErrors.password}
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="showPasswordCheckbox"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                    />
                    <label
                      className="custom-control-label text-muted"
                      htmlFor="showPasswordCheckbox"
                      style={{fontSize: "12.8px"}}
                    >
                      Show password
                    </label>
                  </div>
                  <Link to="/auth/reset-password" className="text-muted" style={{fontSize: "12.8px"}}>
                    Forgot your password?
                  </Link>
                </div>

                <Row className="mt-4">
                  <Col>
                    <Button
                      color="primary"
                      type="submit"
                      block
                      disabled={loading || !!successMessage || isRateLimited}
                      data-color={color}
                    >
                      {loading ? "Signing In..." : "Sign In"}
                    </Button>
                  </Col>
                </Row>

                <div className="text-center mt-3">
                  <p className="text-muted small mt-2">
                    Need to verify your account? <Link to="/auth/verify">Enter Verification Code</Link>
                  </p>
                </div>
              </Form>
            </CardBody>
          </Card>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default SignInForm;
